import Navbar from "@components/Navbar/Navbar";
import { FormControl, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import useAds from "@hooks/useAds";
import useDefaultValues from "@hooks/useDefaultValues";
import { extractDriveId } from "@components/SortableItem/helper";
import useUser from "@hooks/useUser";
import { ctaOptions } from "./constants";
import { notify } from "@utils/notify";
import useRQAdLaunches from "@hooks/useRQAdLaunches";
import { Loader } from "@components/Loader/loader";
import { useNavigate } from "react-router-dom";
import { Copy } from "lucide-react";
import SelectDropdown from "@components/SelectDropdown.jsx/SelectDropdown";
import { fetchConnectedPages } from '@services/getConnectedPages';
import AdPostingValidation from "@components/AdPostingValidation/AdPostingValidation";
import { fetchAdsLaunchValidationErrors } from "@services/validateFbAdsLaunch";

const ReviewDefaultValues = () => {
  const userId = localStorage.getItem("userId");
  const storedAdAccountId = localStorage.getItem("adAccountId");
  const storedAdAccountName = localStorage.getItem("adAccountName");
  const fbCampaignType = localStorage.getItem("campaignObjective");
  const oneAdPerAdSet = localStorage.getItem("oneAdPerAdSet") === "true" ? true : false;
  const storedFacebookPageId = localStorage.getItem("selectedFacebookPageId");
  const storedInstagramPageId = localStorage.getItem("selectedInstagramPageId");
  // New: get selected page names from localStorage
  const storedFacebookPageName = localStorage.getItem("selectedFacebookPageName");
  const storedInstagramPageName = localStorage.getItem("selectedInstagramPageName");

  // states
  const [reviewValues, setReviewValues] = useState({});
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [enabledAds, setEnabledAds] = useState({});
  const [showInstagramWarning, setShowInstagramWarning] = useState({});
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isScheduleLoading, setIsScheduleLoading] = useState(false);
  const [facebookPages, setFacebookPages] = useState([]);
  const [instagramPages, setInstagramPages] = useState([]);
  const [fbPagesOptions, setFbPagesOptions] = useState([]);
  const [instaPagesOptions, setInstaPagesOptions] = useState([]);

  // Add state for validation modal
  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isValidating, setIsValidating] = useState(false);

  // add state for publish validation modal
  const [isPublishValidationModalOpen, setIsPublishValidationModalOpen] = useState(false);
  const [publishValidationErrors, setPublishValidationErrors] = useState([]);
  const [isPublishValidating, setIsPublishValidating] = useState(false);

  // hooks
  const { handleGetAdsWithCreatives } = useAds();
  const { handleGetDefaultValues } = useDefaultValues();
  const { handleGetUserById } = useUser();
  const navigate = useNavigate();
  const { handleSaveAdLaunches } = useRQAdLaunches();

  // selectors
  const { ads } = useSelector((state) => state.ads) ?? {};
  const { defaultValues } = useSelector((state) => state.defaultValues) ?? {};
  const { user } = useSelector((state) => state.user);
  const { createAdCreativeLoading } = useSelector(
    (state) => state.facebookAdCreatives
  );
  const { adAccountId } = user ?? {};

  // Fetch connected pages data using new API
  const getConnectedPagesData = async () => {
    try {
      const response = await fetchConnectedPages({
        user_id: userId,
        force_refresh: false
      });
      if (response?.data?.data?.facebook_pages) {
        setFacebookPages(response.data.data.facebook_pages);
      }
      if (response?.data?.data?.instagram_pages) {
        setInstagramPages(response.data.data.instagram_pages);
      }
    } catch (error) {
      console.error("Error fetching connected pages:", error);
    }
  };

  // handlers
  const handleReviewValuesChange = (adId, e) => {
    const { name, value } = e.target;
    setReviewValues((prev) => {
      const adValues = prev[adId] || {};
      // Handle text inputs differently from select dropdowns
      if (name === "trackingTemplate") {
        return {
          ...prev,
          [adId]: {
            ...adValues,
            [name]: value,
          },
        };
      }
      // Handle select dropdowns
      if (name === "facebookPage") {
        const selectedFbPage = facebookPages.find((page) => page.page_id === value);
        let associatedInstaPage = instagramPages.find(
          (insta) => insta.page_id === selectedFbPage?.page_id
        );
        if (!associatedInstaPage && selectedFbPage) {
          associatedInstaPage = instagramPages.find(
            (insta) => insta.insta_page_name === selectedFbPage.name
          );
          if (!associatedInstaPage) {
            const formattedName = `IGPage_${selectedFbPage.name}`;
            associatedInstaPage = instagramPages.find(
              (insta) => `IGPage_${insta.name}` === formattedName || insta.insta_page_name === formattedName
            );
          }
        }
        const instaOptions = getUniqueOptions();
        let matchingInstaOption = null;
        if (associatedInstaPage) {
          matchingInstaOption = instaOptions.find(opt => opt.id === associatedInstaPage.insta_id);
        }
        setShowInstagramWarning(prev => ({
          ...prev,
          [adId]: false
        }));
        if (!associatedInstaPage) {
          notify("No Instagram page linked to selected Facebook page", "warning");
        }
        return {
          ...prev,
          [adId]: {
            ...adValues,
            [name]: { id: value },
            instagramPage: matchingInstaOption
              ? { id: matchingInstaOption.id }
              : { id: '' },
          },
        };
      }
      if (name === "instagramPage") {
        const selectedInstaPage = instagramPages.find(page => page.insta_id === value);
        const currentFbPageId = adValues.facebookPage?.id;
        if (selectedInstaPage && selectedInstaPage.page_id !== currentFbPageId) {
          setShowInstagramWarning(prev => ({
            ...prev,
            [adId]: true
          }));
          notify("This Instagram page is not linked with the selected Facebook page", "error");
        } else {
          setShowInstagramWarning(prev => ({
            ...prev,
            [adId]: false
          }));
        }
      }
      return {
        ...prev,
        [adId]: {
          ...adValues,
          [name]: { id: value },
        },
      };
    });
  };

  const prepareAdLaunchRequest = () => {
    if (!ads || !defaultValues || !reviewValues) {
      notify("Missing required data for ad launch", "error");
      return null;
    }


    // Check if we have payload from advanced mapping
    const publishPayloadFromAdvMapping = localStorage.getItem("publishPayloadFromAdvMapping");
    
    if (publishPayloadFromAdvMapping) {
      // If we have advanced mapping data, use that as the base
      try {
        const advMappingPayload = JSON.parse(publishPayloadFromAdvMapping);
        
        if (advMappingPayload && advMappingPayload.adObjects && advMappingPayload.adObjects.length > 0) {
          // Merge the review values with the advanced mapping payload
          const adObjects = advMappingPayload.adObjects.map(adObj => {
            const adReview = reviewValues[adObj.adId] || {};
            
            return {
              ...adObj,
              fbPageId: adReview?.facebookPage?.id || adObj.fbPageId || "",
              fbCallToAction: adReview?.callToAction?.id || "",
              fbInstagramId: adReview?.instagramPage?.id || adObj.fbInstagramId || "",
              trackingTemplate: adReview?.trackingTemplate || "",
              deepLink: adReview?.deepLink || "",
              fbAdLiveStatus: enabledAds[adObj.adId] === true ? "ACTIVE" : "PAUSED",
              oneAdPerAdset: oneAdPerAdSet
            };
          });
          
          return { adObjects };
        }
      } catch (error) {
        console.error("Error parsing advanced mapping payload:", error);
        notify("Error processing advanced mapping data", "error");
      }
    }


    const adObjects = ads.map((ad) => {
      const adReview = reviewValues[ad._id] || {};
      return {
        adId: ad._id,
        fbAdsetId: defaultValues?.targetAdSetId || "",
        fbAdsetName: defaultValues?.fbAdsetName || "",
        fbPageId: adReview?.facebookPage?.id || "",
        fbCallToAction: adReview?.callToAction?.id || "",
        fbInstagramId: adReview?.instagramPage?.id || "",
        fbAdAccountId: storedAdAccountId || adAccountId,
        fbCampaignName: defaultValues?.fbCampaignName || "",
        fbCampaignId: defaultValues?.targetCampaignId || "",
        userId: userId,
        oneAdPerAdset: oneAdPerAdSet || false,
        fbCampaignType: fbCampaignType || "OUTCOME_SALES",
        trackingTemplate: adReview?.trackingTemplate || "",
        fbAdLiveStatus: enabledAds[ad._id] === true ? "ACTIVE" : "PAUSED"
      };
    });
    return { adObjects };
  };

  const handleSaveAndPublish = async (validate = false) => {
    const payload = prepareAdLaunchRequest();
    if (!payload) {
      notify("Failed to prepare ad launch data", "error");
      return;
    }
    if (validate) {
      // First, check for validation errors using the API
      const errors = await checkForValidationErrors(payload);
      if (errors.length > 0) {
        setPublishValidationErrors(errors);
        setIsPublishValidationModalOpen(true);
        return;
      }
    }

    // If no errors, proceed with save and publish
    setIsDataLoading(true);
    try {
      const response = await handleSaveAdLaunches(payload);
      if (response && response.batch_id) {
        navigate("/ads-progress", {
          state: {
            batchId: response.batch_id,
            launches: response.launches,
          },
        });
      } else {
        notify("Failed to create ad launches. Please try again.", "error");
      }
    } catch (error) {
      console.error("Error during ad launch:", error);
      notify("An error occurred while creating ad launches", "error");
    } finally {
      setIsDataLoading(false);
    }
  };

  // Add validation check function
  const checkForValidationErrors = async (schedulePayload) => {
    try {
      setIsValidating(true);
      const response = await fetchAdsLaunchValidationErrors(schedulePayload);

      if (response.data && response.data.validation && !response.data.validation.is_valid) {
        const errors = response.data.validation.error.map((error, index) => ({
          id: index + 1,
          error: error,
          errorDetails: response.data.validation.error_details[index] || "",
          action: response.data.validation.actions[index] || ""
        }));
        return errors;
      }

      return [];
    } catch (error) {
      console.error("Error validating ads launch:", error);
      notify("Error validating ads launch", "error");
      return [];
    } finally {
      setIsValidating(false);
    }
  };

  const handleScheduleAds = async () => {
    // Prepare the request with the correct format
    const payload = prepareAdLaunchRequest();
    if (!payload) {
      notify("Failed to prepare ad launch data", "error");
      return;
    }

    const schedulePayload = {
      adObjects: payload.adObjects.map(adObject => ({
        ...adObject,
        fbAdLiveStatus: "ACTIVE"
      }))
    };

    console.log("schedulePayload", schedulePayload);

    // First, check for validation errors using the API
    const errors = await checkForValidationErrors(schedulePayload);

    if (errors.length > 0) {
      // Show validation modal if there are errors
      setValidationErrors(errors);
      setIsValidationModalOpen(true);
      return;
    }

    // If no errors, proceed with original scheduling logic
    const userConfirmed = window.confirm("Are you sure you want to schedule these ads?");
    if (userConfirmed) {
      setIsScheduleModalOpen(true);
    }
  };

  // Handle validation modal actions
  const handleValidationEditAds = () => {
    setIsValidationModalOpen(false);

    // Proceed with scheduling despite validation errors
    const userConfirmed = window.confirm("Are you sure you want to schedule these ads?");
    if (userConfirmed) {
      setIsScheduleModalOpen(true);
    }
  };

  const handlePublishValidationEditAds = () => {
    setIsPublishValidationModalOpen(false);

    // Proceed with publishing despite validation errors
    const userConfirmed = window.confirm("Are you sure you want to publish these ads?");
    if (userConfirmed) {
      handleSaveAndPublish(false);
    }
  };

  const handleValidationResolve = async () => {
    setIsValidationModalOpen(false);

    // Prepare the same payload for re-validation
    const payload = prepareAdLaunchRequest();

    // Re-check validation and proceed if resolved
    const errors = await checkForValidationErrors(payload);
    if (errors.length === 0) {
      // If no more errors, proceed with scheduling
      const userConfirmed = window.confirm("Are you sure you want to schedule these ads?");
      if (userConfirmed) {
        setIsScheduleModalOpen(true);
      }
    } else {
      // If errors still exist, show the validation modal again
      setValidationErrors(errors);
      setIsValidationModalOpen(true);
    }
  };

  const handlePublishValidationResolve = async () => {
    setIsPublishValidationModalOpen(false);
    handleSaveAndPublish(true);
  };

  const handleScheduleDateConfirm = async (scheduledDateTime) => {
    if (isNaN(scheduledDateTime.getTime()) || scheduledDateTime <= new Date()) {
      notify("Please enter a valid future date and time", "error");
      return;
    }

    setIsScheduleLoading(true);

    const minutesUntilLaunch = Math.ceil((scheduledDateTime.getTime() - Date.now()) / 60000);
    const payload = prepareAdLaunchRequest();
    if (!payload) {
      notify("Failed to prepare ad launch data", "error");
      setIsScheduleLoading(false);
      return;
    }
    const schedulePayload = {
      adObjects: payload.adObjects.map(adObject => ({
        ...adObject,
        launchInMinutes: minutesUntilLaunch,
        fbAdLiveStatus: "ACTIVE"
      }))
    };

    console.log("post", schedulePayload);
    try {
      const response = await handleSaveAdLaunches(schedulePayload);
      if (response && response.batch_id) {
        const timeText =
          minutesUntilLaunch === 1
            ? "1 minute"
            : minutesUntilLaunch < 60
              ? `${minutesUntilLaunch} minutes`
              : `${Math.floor(minutesUntilLaunch / 60)} hour${Math.floor(minutesUntilLaunch / 60) !== 1 ? "s" : ""
              } and ${minutesUntilLaunch % 60} minute${minutesUntilLaunch % 60 !== 1 ? "s" : ""
              }`;
        navigate("/ads-progress", {
          state: {
            batchId: response.batch_id,
            launches: response.launches,
            isScheduled: true,
            scheduledTime: scheduledDateTime.toISOString(),
            scheduledUnixTime: Math.floor(scheduledDateTime.getTime() / 1000),
            launchInMinutes: minutesUntilLaunch,
            displayScheduledTime: timeText
          },
        });
        // notify(
        //   `Ads scheduled successfully for ${scheduledDateTime.toLocaleString()} (in ${timeText})`,
        //   "success"
        // );

        // Close modal and navigate to launch history
        setIsScheduleModalOpen(false);
      } else {
        notify("Failed to schedule ad launches. Please try again.", "error");
      }
    } catch (error) {
      console.error("Error during ad scheduling:", error);
      notify("An error occurred while scheduling ad launches", "error");
    } finally {
      setIsScheduleLoading(false);
    }
  };

  const handleToggleAdEnabled = (adId) => {
    setEnabledAds((prev) => ({
      ...prev,
      [adId]: prev[adId] === false ? true : false,
    }));
  };

  useEffect(() => {
    if (userId) {
      getConnectedPagesData();
    }
  }, [userId]);

  useEffect(() => {
    if (facebookPages && facebookPages.length > 0) {
      const options = facebookPages.map(page => ({
        id: page.page_id,
        name: page.name
      }));
      setFbPagesOptions(options);
    }
  }, [facebookPages]);

  useEffect(() => {
    if (instagramPages && instagramPages.length > 0) {
      const options = instagramPages.map(page => ({
        id: page.insta_id,
        displayName: page.insta_page_name || `IGPage_${page.name}`
      }));
      setInstaPagesOptions(options);
    }
  }, [instagramPages]);

  useEffect(() => {
    setIsDataLoading(true);
    handleGetUserById(userId);
  }, []);

  useEffect(() => {
    if (userId) {
      handleGetDefaultValues(userId, storedAdAccountId);
    }
  }, [userId, storedAdAccountId]);

  useEffect(() => {
    if (userId && defaultValues?.creativeLocationId) {
      handleGetAdsWithCreatives(userId, defaultValues?.creativeLocationId);
    }
  }, [userId, defaultValues]);

  useEffect(() => {
    if (!defaultValues || !ads || !ads.length) return;
    // Facebook Page selection logic (only localStorage and API data)
    let facebookPage = "";
    if (storedFacebookPageId && facebookPages && facebookPages.length > 0) {
      const matchedFbAccount = facebookPages.find(
        (page) => page.page_id === storedFacebookPageId
      );
      facebookPage = matchedFbAccount?.page_id || "";
    }
    // Instagram Page selection logic: match page_id with selected fb page, else empty
    let instagramPage = "";
    if (facebookPage && instagramPages && instagramPages.length > 0) {
      const matchedInsta = instagramPages.find(
        (page) => page.page_id === facebookPage
      );
      instagramPage = matchedInsta?.insta_id || "";
      if (!matchedInsta) {
        notify("No Instagram page linked to selected Facebook page", "warning");
      }
    }
    const callToAction =
      ctaOptions.find(
        (option) => option.text === defaultValues?.callToAction
      ) || null;
    const initialValues = {};
    ads.forEach((ad) => {
      initialValues[ad._id] = {
        callToAction: callToAction ? { id: callToAction.id } : { id: "" },
        trackingTemplate: defaultValues.trackingTemplate || "",
        facebookPage: facebookPage ? { id: facebookPage } : { id: "" },
        instagramPage: instagramPage ? { id: instagramPage } : { id: "" },
      };
      setEnabledAds(prev => ({
        ...prev,
        [ad._id]: false
      }));
    });
    setReviewValues(initialValues);
  }, [defaultValues, ads, facebookPages, instagramPages]);

  useEffect(() => {
    const hasAds = ads && ads.length > 0;
    const hasDefaultValues = !!defaultValues;
    const hasFacebookPages = facebookPages && facebookPages.length > 0;
    const hasInstagramPages = instagramPages && instagramPages.length > 0;
    if (
      hasAds &&
      hasDefaultValues &&
      hasFacebookPages &&
      hasInstagramPages
    ) {
      setIsDataLoading(false);
    }
  }, [ads, defaultValues, facebookPages, instagramPages]);

  const getUniqueOptions = () => {
    const uniqueOptions = new Map();
    if (instagramPages && instagramPages.length > 0) {
      instagramPages.forEach(page => {
        if (page?.insta_id && page?.insta_page_name) {
          uniqueOptions.set(page.insta_id, {
            id: page.insta_id,
            displayName: page.insta_page_name
          });
        } else if (page?.insta_id && page?.name) {
          uniqueOptions.set(page.insta_id, {
            id: page.insta_id,
            displayName: `IGPage_${page.name}`
          });
        }
      });
    }
    const result = Array.from(uniqueOptions.values()).filter(option =>
      option.displayName && option.displayName.trim() !== ''
    );
    return result;
  };

  return (
    <>
      {(isDataLoading || createAdCreativeLoading || isValidating) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <Loader />
        </div>
      )}
      <div className="flex flex-col min-h-screen gap-4 p-6 mx-auto bg-white max-w-7xl">
        <Navbar
          title="Review Default Values"
          onSaveAndNext={() => handleSaveAndPublish(true)}
          onSchedule={handleScheduleAds}
          isSaving={false}
          showBack={true}
          showSaveAndNext={true}
          showSchedule={true}
          saveButtonText="Save and Publish"
          scheduleButtonText="Schedule Ads"
          adAccountName={storedAdAccountName}
          loading={isScheduleModalOpen || isScheduleLoading}
        />
        <div className="p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full table-fixed">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-4 py-3 text-sm font-medium text-left text-gray-600 w-28">Creative</th>
                  <th className="w-40 px-4 py-3 text-sm font-medium text-left text-gray-600">Ad Name</th>
                  <th className="w-40 px-4 py-3 text-sm font-medium text-left text-gray-600">Call to Action</th>
                  <th className="px-4 py-3 text-sm font-medium text-left text-gray-600 w-60">Tracking Template</th>
                  <th className="w-40 px-4 py-3 text-sm font-medium text-left text-gray-600">Facebook Page</th>
                  <th className="w-40 px-4 py-3 text-sm font-medium text-left text-gray-600">Instagram Page</th>
                  <th className="w-32 px-4 py-3 text-sm font-medium text-left text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {ads?.map(
                  (ad) =>
                    ad.creatives.length > 0 && (
                      <tr
                        key={ad._id}
                        className="border-b border-gray-100 hover:bg-gray-50"
                      >
                        <td className="px-4 py-4 w-28">
                          {ad.creatives?.length > 0 ? (
                            <img
                              src={ad.creatives[0].driveThumbnail || `https://drive.google.com/thumbnail?sz=w456-h200&id=${extractDriveId(
                                ad.creatives[0].fileLink
                              )}`}
                              alt={ad.creatives[0].fileName}
                              className="object-cover rounded w-28 h-28"
                            />
                          ) : (
                            <div className="flex items-center justify-center w-16 h-16 bg-gray-200 rounded">
                              <span className="text-xs text-gray-500">
                                No creative
                              </span>
                            </div>
                          )}
                        </td>
                        <td className="w-40 px-4 py-4">
                          <span className="break-words line-clamp-3">{ad.name}</span>
                        </td>
                        <td className="w-48 px-4 py-4">
                          <div className="flex flex-col gap-1">
                            <div className="flex justify-center w-full">
                              <FormControl fullWidth size="small">
                                <SelectDropdown
                                  name="callToAction"
                                  value={reviewValues?.[ad?._id]?.callToAction?.id || ""}
                                  onChange={(e) => handleReviewValuesChange(ad._id, e)}
                                  options={ctaOptions}
                                  optionValueKey="id"
                                  optionLabelKey="text"
                                  sx={{
                                    backgroundColor: "white",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "rgb(209 213 219)",
                                    },
                                  }}
                                />
                              </FormControl>
                            </div>
                            {ads?.indexOf(ad) === 0 && reviewValues?.[ad?._id]?.callToAction?.id && (
                              <button
                                onClick={() => {
                                  const selectedCTA = reviewValues?.[ad?._id]?.callToAction?.id;
                                  if (!selectedCTA) return;
                                  const updatedValues = { ...reviewValues };
                                  ads.forEach((adItem) => {
                                    if (updatedValues[adItem._id]) {
                                      updatedValues[adItem._id] = {
                                        ...updatedValues[adItem._id],
                                        callToAction: { id: selectedCTA },
                                      };
                                    }
                                  });
                                  setReviewValues(updatedValues);
                                }}
                                className="flex items-center justify-center w-full gap-1 px-2 py-1 text-xs font-medium text-blue-600 transition-colors duration-200 border border-blue-200 rounded-md bg-blue-50 hover:bg-blue-100"
                              >
                                <Copy size={11} />
                                Apply to all
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 w-60">
                          <div className="flex flex-col gap-1">
                            <div className="flex flex-col w-full">
                              <div className="flex items-center justify-center w-full gap-1">
                                <TextField
                                  variant="outlined"
                                  size="large"
                                  className="w-full"
                                  placeholder="Enter tracking template"
                                  value={reviewValues?.[ad?._id]?.trackingTemplate || ""}
                                  onChange={(e) => handleReviewValuesChange(ad._id, e)}
                                  name="trackingTemplate"
                                  multiline
                                  InputProps={{
                                    style: {
                                      padding: "0px 4px",
                                      borderColor: "rgb(209 213 219)",
                                      overflow: "auto",
                                      maxHeight: "56px",
                                    },
                                  }}
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      "& fieldset": {
                                        borderColor: "rgb(209 213 219)",
                                      },
                                      "&:hover fieldset": {
                                        borderColor: "rgb(209 213 219)",
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor: "blue",
                                      },
                                      "& .MuiInputBase-input": {
                                        overflow: "auto !important",
                                        maxHeight: "56px",
                                        scrollbarWidth: "thin",
                                        "&::-webkit-scrollbar": {
                                          width: "4px",
                                        },
                                        "&::-webkit-scrollbar-thumb": {
                                          backgroundColor: "rgba(0,0,0,.2)",
                                          borderRadius: "4px",
                                        },
                                      },
                                    },
                                  }}
                                />
                              </div>
                              {ads?.indexOf(ad) === 0 && reviewValues?.[ad?._id]?.trackingTemplate && (
                                <div className="flex justify-center w-full mt-1">
                                  <button
                                    onClick={() => {
                                      const trackingTemplate = reviewValues?.[ad?._id]?.trackingTemplate;
                                      if (!trackingTemplate) return;
                                      const updatedValues = { ...reviewValues };
                                      ads.forEach((adItem) => {
                                        if (updatedValues[adItem._id]) {
                                          updatedValues[adItem._id] = {
                                            ...updatedValues[adItem._id],
                                            trackingTemplate: trackingTemplate,
                                          };
                                        }
                                      });
                                      setReviewValues(updatedValues);
                                    }}
                                    className="flex items-center justify-center w-full gap-1 px-2 py-1 text-xs font-medium text-blue-600 transition-colors duration-200 border border-blue-200 rounded-md bg-blue-50 hover:bg-blue-100"
                                  >
                                    <Copy size={11} />
                                    Apply to all
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="w-40 px-4 py-4">
                          <div className="flex flex-col gap-1">
                            <div className="flex justify-center w-full">
                              <FormControl fullWidth size="small">
                                <SelectDropdown
                                  name="facebookPage"
                                  value={reviewValues?.[ad?._id]?.facebookPage?.id || ""}
                                  onChange={(e) => handleReviewValuesChange(ad._id, e)}
                                  options={facebookPages.map(page => ({ id: page.page_id, name: page.name }))}
                                  optionValueKey="id"
                                  optionLabelKey="name"
                                  sx={{
                                    backgroundColor: "white",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "rgb(209 213 219)",
                                    },
                                  }}
                                />
                              </FormControl>
                            </div>
                            {ads?.indexOf(ad) === 0 && (
                              <button
                                onClick={() => {
                                  const selectedFbPage =
                                    reviewValues?.[ad?._id]?.facebookPage?.id;
                                  if (!selectedFbPage) return;
                                  const updatedValues = { ...reviewValues };
                                  ads.forEach((adItem) => {
                                    if (updatedValues[adItem._id]) {
                                      updatedValues[adItem._id] = {
                                        ...updatedValues[adItem._id],
                                        facebookPage: { id: selectedFbPage },
                                      };
                                    }
                                  });
                                  setReviewValues(updatedValues);
                                }}
                                className="flex items-center justify-center w-full gap-1 px-2 py-1 text-xs font-medium text-blue-600 transition-colors duration-200 border border-blue-200 rounded-md bg-blue-50 hover:bg-blue-100"
                              >
                                <Copy size={11} />
                                Apply to all
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="w-40 px-4 py-4">
                          <div className="flex flex-col gap-1">
                            <div className="flex justify-center w-full">
                              <FormControl fullWidth size="small">
                                <SelectDropdown
                                  name="instagramPage"
                                  value={reviewValues?.[ad?._id]?.instagramPage?.id || ""}
                                  onChange={(e) => handleReviewValuesChange(ad._id, e)}
                                  options={instagramPages.map(page => ({ id: page.insta_id, name: page.insta_page_name }))}
                                  optionValueKey="id"
                                  optionLabelKey="name"
                                  sx={{
                                    backgroundColor: "white",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "rgb(209 213 219)",
                                    },
                                  }}
                                />
                              </FormControl>
                            </div>
                            {ads?.indexOf(ad) === 0 && (
                              <button
                                onClick={() => {
                                  const selectedInstaPage =
                                    reviewValues?.[ad?._id]?.instagramPage?.id;
                                  if (!selectedInstaPage) return;
                                  const updatedValues = { ...reviewValues };
                                  ads.forEach((adItem) => {
                                    if (updatedValues[adItem._id]) {
                                      updatedValues[adItem._id] = {
                                        ...updatedValues[adItem._id],
                                        instagramPage: { id: selectedInstaPage },
                                      };
                                    }
                                  });
                                  setReviewValues(updatedValues);
                                }}
                                className="flex items-center justify-center w-full gap-1 px-2 py-1 text-xs font-medium text-blue-600 transition-colors duration-200 border border-blue-200 rounded-md bg-blue-50 hover:bg-blue-100"
                              >
                                <Copy size={11} />
                                Apply to all
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="w-32 px-4 py-4">
                          <div className="flex flex-col w-full">
                            <div className="flex items-center gap-2 my-4">
                              <div
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none cursor-pointer ${enabledAds[ad._id] === true ? 'bg-blue-600' : 'bg-gray-200'}`}
                                onClick={() => handleToggleAdEnabled(ad._id)}
                                role="switch"
                                aria-checked={enabledAds[ad._id] === true}
                                tabIndex={0}
                              >
                                <span
                                  className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${enabledAds[ad._id] === true ? 'translate-x-6' : 'translate-x-1'}`}
                                />
                              </div>
                              <span className="ml-2 text-sm font-medium text-gray-700">
                                {enabledAds[ad._id] === true ? "On" : "Off"}
                              </span>
                            </div>
                            {ads?.indexOf(ad) === 0 && (
                              <button
                                onClick={() => {
                                  const currentStatus = enabledAds[ad._id];
                                  const updatedEnabledAds = { ...enabledAds };
                                  ads.forEach((adItem) => {
                                    updatedEnabledAds[adItem._id] = currentStatus;
                                  });
                                  setEnabledAds(updatedEnabledAds);
                                }}
                                className="flex items-center justify-center w-full gap-1 px-2 py-1 text-xs font-medium text-blue-600 transition-colors duration-200 border border-blue-200 rounded-md bg-blue-50 hover:bg-blue-100"
                              >
                                <Copy size={11} />
                                Apply to all
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {/* Date Time Picker Modal */}
      {isScheduleModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="p-6 bg-white rounded-lg shadow-lg w-96">
            <h2 className="mb-4 text-xl font-semibold">Schedule Ads</h2>
            <div className="mb-4">
              <label htmlFor="scheduleDateTime" className="block mb-1 text-sm font-medium text-gray-700">
                Select date and time
              </label>
              <input
                id="scheduleDateTime"
                type="datetime-local"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                defaultValue={(() => {
                  const date = new Date();
                  date.setMinutes(date.getMinutes());
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  const hours = String(date.getHours()).padStart(2, '0');
                  const minutes = String(date.getMinutes()).padStart(2, '0');
                  return `${year}-${month}-${day}T${hours}:${minutes}`;
                })()}
                min={(() => {
                  const date = new Date();
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  const hours = String(date.getHours()).padStart(2, '0');
                  const minutes = String(date.getMinutes()).padStart(2, '0');
                  return `${year}-${month}-${day}T${hours}:${minutes}`;
                })()}
                step="60"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setIsScheduleModalOpen(false);
                  notify("Scheduling cancelled", "info");
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                disabled={isScheduleLoading}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const dateTimeInput = document.getElementById('scheduleDateTime');
                  const selectedDateTime = new Date(dateTimeInput.value);
                  handleScheduleDateConfirm(selectedDateTime);
                }}
                className="flex items-center gap-2 px-4 py-2 text-white bg-green-600 rounded-md hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed"
                disabled={isScheduleLoading}
              >
                {isScheduleLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white rounded-full border-t-transparent animate-spin"></div>
                    Scheduling...
                  </>
                ) : (
                  'Schedule'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ad Posting Validation Modal */}
      {isValidationModalOpen && validationErrors.length > 0 && (
        <AdPostingValidation
          isOpen={isValidationModalOpen}
          onClose={() => setIsValidationModalOpen(false)}
          errors={validationErrors}
          onEditAds={handleValidationEditAds}
          onResolveErrors={handleValidationResolve}
        />
      )}
      {isPublishValidationModalOpen && publishValidationErrors.length > 0 && (
        <AdPostingValidation
          isOpen={isPublishValidationModalOpen}
          onClose={() => setIsPublishValidationModalOpen(false)}
          errors={publishValidationErrors}
          onEditAds={handlePublishValidationEditAds}
          onResolveErrors={handlePublishValidationResolve}
        />
      )}
    </>
  );
};

export default ReviewDefaultValues;
